import React, { ReactElement, useState } from 'react';
import { PaginationState } from 'app/shared/pagination/pagination.model';
import { createInitPagination, renderPaginationFooter } from 'app/shared/pagination/pagination.util';
import { sortByColumnWithoutUpdateUtil } from 'app/shared/util/table-util';
import { Alert, Table } from 'reactstrap';
import ViewTableHeaderColumn, { IViewTableHeaderColumnProps } from 'app/shared/view-table/view-table-header-column';
import { isArray } from 'app/shared/util/common';
import { Pagination } from 'edozvola-frontend-lib/dist/ui';
import { Loader } from 'app/shared/loader/loader';
import { useFormik } from 'formik';

interface IServerManagedData {
  initialPaginationColumn: string;
  initialSortDirection?: string;
  fetchDataQuery: any;
  fetchDataProps?: {};
  tableData?: never;
  dataLength?: never;
  pagination?: never;
  onPageChange?: never;
  onSizeChange?: never;
}

interface IFrontendManagedData {
  initialPaginationColumn?: never;
  initialSortDirection?: string;
  fetchDataQuery?: never;
  fetchDataProps?: never;
  tableData: any;
  dataLength: number;
  pagination?: PaginationState;
  onPageChange?: any;
  onSizeChange?: any;
}

interface IAdditionalProps {
  renderTableRow: Function;
  children: ReactElement<typeof ViewTableHeaderColumn> | ReactElement<typeof ViewTableHeaderColumn>[];
  noDataMessage: string;
  autoLayout?: boolean;
}

export type IViewTable = IAdditionalProps & (IServerManagedData | IFrontendManagedData);

const ManagableTable: React.FC<IViewTable> = ({
  fetchDataQuery,
  fetchDataProps,
  tableData,
  dataLength,
  initialPaginationColumn,
  initialSortDirection,
  renderTableRow,
  noDataMessage,
  pagination: propPagination,
  onPageChange: propOnPageChange,
  onSizeChange: propOnSizeChange,
  children,
  autoLayout,
}) => {
  const [pagination, setPagination] = useState<PaginationState>(
    propPagination ? propPagination : createInitPagination(initialPaginationColumn, initialSortDirection),
  );

  const { data, isLoading } = fetchDataQuery
    ? fetchDataProps
      ? fetchDataQuery(pagination, fetchDataProps)
      : fetchDataQuery(pagination)
    : {
        data: {
          content: tableData,
          totalElements: dataLength,
        },
        isLoading: false,
      };

  const { values, handleChange, setFieldValue } = useFormik({
    initialValues: data?.content,
    onSubmit: console.log,
    enableReinitialize: true,
  });

  const onPageChange = (page: number) => {
    if (propOnPageChange) {
      propOnPageChange(page);
    }
    setPagination(s => ({ ...s, page }));
  };
  const onSizeChange = (size: number) => {
    if (propOnSizeChange) {
      propOnSizeChange(size);
    }
    setPagination(s => ({ ...s, page: 1, size }));
  };
  const sortByColumn = (event: React.MouseEvent<HTMLDivElement>) => sortByColumnWithoutUpdateUtil(event, pagination, setPagination);

  const totalElements = data?.totalElements;

  const renderManagableTable = () => {
    return <tbody>{values && values?.map((value, index) => renderTableRow(value, index, setFieldValue))}</tbody>;
  };

  return (
    <>
      {isLoading && <Loader />}
      {data?.content.length === 0 ? (
        <Alert color="info" className="uniform-width">
          {noDataMessage}
        </Alert>
      ) : (
        <>
          {!isLoading && (
            <>
              <Table size={'sm'} bordered={true} striped={true} responsive={true} style={{ tableLayout: autoLayout ? 'auto' : 'fixed' }}>
                <thead>
                  <tr>
                    {isArray(children) &&
                      children.map((child, index) => (
                        <React.Fragment key={index}>
                          {React.isValidElement(child)
                            ? React.cloneElement(child, {
                                pagination: pagination,
                                sortFunction: sortByColumn,
                              } as IViewTableHeaderColumnProps)
                            : null}
                        </React.Fragment>
                      ))}
                    {!isArray(children) && (
                      <React.Fragment>
                        {React.isValidElement(children)
                          ? React.cloneElement(children, {
                              pagination: pagination,
                              sortFunction: sortByColumn,
                            } as IViewTableHeaderColumnProps)
                          : null}
                      </React.Fragment>
                    )}
                  </tr>
                </thead>
                {renderManagableTable()}
              </Table>
              <div className="row">
                <div className="col-lg-6 d-flex justify-content-start">
                  {totalElements && totalElements > 10 && (
                    <Pagination
                      page={pagination.page}
                      size={pagination.size}
                      total={totalElements}
                      onChange={onPageChange}
                      onSizeChange={onSizeChange}
                      sizeOptions={[10, 25, 50]}
                      className="float-left"
                    />
                  )}
                </div>
                <div className="col-lg-6 text-end">
                  <h6>{renderPaginationFooter({ ...pagination, total: totalElements })}</h6>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </>
  );
};

export default ManagableTable;
